import { Link } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ContactForm from './components/ContactForm';

const channels = [
  {
    id: 'channel-email',
    icon: 'ri-mail-line',
    title: '编辑部邮箱',
    value: 'editor@meirichigua.com',
    note: '适合投稿、指正与商务合作，两个工作日内回复。',
  },
  {
    id: 'channel-tip',
    icon: 'ri-shield-keyhole-line',
    title: '匿名线索通道',
    value: '使用本页表单提交',
    note: '可以不填写真实姓名，只需留下可联络的邮箱以便核对。',
  },
  {
    id: 'channel-time',
    icon: 'ri-time-line',
    title: '工作时间',
    value: '周一至周五 10:00 - 19:00',
    note: '节假日期间的来信会在下一个工作日统一处理。',
  },
];

export default function ContactPage() {
  useSeo({
    title: '联系我们 | 美日吃瓜 线索投递与内容指正',
    description:
      '向美日吃瓜提供爆料线索、指正稿件内容或洽谈合作。支持匿名提交，只需留下可联络邮箱，编辑部会在两个工作日内回复核对。',
    keywords: '美日吃瓜联系,爆料线索投递,内容指正,匿名投稿,编辑部邮箱',
    path: '/contact',
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'ContactPage',
          name: '联系美日吃瓜',
          url: '/contact',
          inLanguage: 'zh-CN',
          description: '面向读者的线索投递与内容指正通道。',
        },
        {
          '@type': 'Organization',
          name: '美日吃瓜',
          url: '/',
          email: 'editor@meirichigua.com',
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'editorial',
            email: 'editor@meirichigua.com',
            availableLanguage: 'zh-CN',
          },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
            { '@type': 'ListItem', position: 2, name: '联系我们', item: '/contact' },
          ],
        },
      ],
    },
  });

  return (
    <>
      <Breadcrumbs items={[{ label: '联系我们' }]} />

      <section className="border-b border-background-300/70 bg-background-100">
        <div className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-14">
          <p className="mb-3 font-label text-xs uppercase tracking-[0.32em] text-primary-600">
            Contact
          </p>
          <h1 className="max-w-3xl font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
            有线索、有疑问，或者发现我们写错了
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            我们欢迎三种来信：新的爆料线索、对已发布稿件的指正，以及内容授权与合作咨询。线索无论大小都值得被核对，哪怕只是一份看起来普通的招标文件。
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="mb-5 font-heading text-lg font-bold text-foreground-950">
              投递线索
            </h2>
            <ContactForm />
          </div>

          <aside className="flex flex-col gap-5">
            {channels.map((channel) => (
              <div
                key={channel.id}
                className="rounded-lg border border-background-300/70 bg-background-100 p-5"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-lg text-primary-700">
                  <i className={channel.icon} />
                </span>
                <h2 className="mt-3 font-heading text-sm font-bold text-foreground-950">
                  {channel.title}
                </h2>
                <p className="mt-1.5 font-label text-sm text-primary-700">{channel.value}</p>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{channel.note}</p>
              </div>
            ))}

            <div className="rounded-lg bg-secondary-900 p-5">
              <h2 className="font-heading text-sm font-bold text-background-50">投递前请注意</h2>
              <ul className="mt-3 space-y-2.5 text-sm leading-relaxed text-secondary-200">
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-accent-400">
                    <i className="ri-checkbox-circle-line" />
                  </span>
                  尽量说明材料的来源与可查编号
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-accent-400">
                    <i className="ri-checkbox-circle-line" />
                  </span>
                  我们只承诺核对，不承诺必然成稿
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-accent-400">
                    <i className="ri-checkbox-circle-line" />
                  </span>
                  涉及人身安全的线索可要求全程匿名
                </li>
              </ul>
              <Link
                to="/faq"
                className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap font-label text-sm font-medium text-accent-300 transition-colors hover:text-accent-200"
              >
                查看常见问题
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-right-line" />
                </span>
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}